/**
 * Custom Pricing with Draft Orders API
 * Creates draft orders with individual custom prices
 */

// Global variables
window.customPricingProcessing = false;
window.customPricingData = {};

// Initialize custom pricing data
function initializeCustomPricingData() {
  if (!window.customPricingData.initialized) {
    window.customPricingData = {
      initialized: true,
      shop: window.Shopify ? window.Shopify.shop : '',
      cart: {},
      customer: {},
      custom_prices: {},
      metafields_cache: {}
    };
  }
}

// Fetch product metafields
async function fetchProductMetafields(productHandle) {
  try {
    // console.log(`🔍 Fetching metafields for product: ${productHandle}`);
    const response = await fetch(`/products/${productHandle}.js`);
    const product = await response.json();
    
    // console.log(`📦 Product data received:`, product);
    // console.log(`📊 Variants count: ${product.variants?.length || 0}`);
    
    if (product.variants) {
      for (const variant of product.variants) {
        // console.log(`🔍 Checking variant ${variant.id} for metafields...`);
        // console.log(`   Metafields present: ${!!variant.metafields}`);
        
        if (variant.metafields) {
          // console.log(`   Metafields data:`, variant.metafields);
          window.customPricingData.metafields_cache[variant.id] = variant.metafields;
        } else {
          // console.log(`❌ No metafields found in variant ${variant.id}`);
        }
      }
    }
    
    return product;
  } catch (error) {
    console.error('❌ Error fetching product metafields:', error);
    return null;
  }
}

// Get custom price from metafields
function getCustomPriceFromMetafields(variantId, customerTag) {
  // console.log(`🔍 Looking for metafield: variant ${variantId}, tag ${customerTag}`);
  
  const metafields = window.customPricingData.metafields_cache[variantId];
  // console.log(`📋 Cached metafields for variant ${variantId}:`, metafields);
  
  if (!metafields) {
    // console.log(`❌ No metafields cached for variant ${variantId}`);
    return null;
  }
  
  const metafieldKey = `custom_prices.${customerTag}`;
  // console.log(`🔑 Looking for metafield key: ${metafieldKey}`);
  
  const customPrice = metafields[metafieldKey];
  // console.log(`💰 Metafield value found: ${customPrice}`);
  
  if (customPrice && parseFloat(customPrice) > 0) {
    const price = parseFloat(customPrice);
    // console.log(`✅ Valid custom price found: $${price}`);
    return price; // Return in dollars
  }
  
  // console.log(`❌ No valid custom price found`);
  return null;
}

// Try to get metafields from page data (if available)
function tryGetMetafieldsFromPageData(variantId) {
  // console.log(`🔍 Trying to get metafields from page data for variant: ${variantId}`);
  
  // Check if metafields are available in window.productMetafields
  if (window.productMetafields && window.productMetafields[variantId]) {
    // console.log(`✅ Found metafields in window.productMetafields:`, window.productMetafields[variantId]);
    window.customPricingData.metafields_cache[variantId] = window.productMetafields[variantId];
    return window.productMetafields[variantId];
  }
  
  // Check if we can find metafields in any global product data
  if (window.product && window.product.variants) {
    const variant = window.product.variants.find(v => v.id == variantId);
    if (variant && variant.metafields) {
      // console.log(`✅ Found metafields in window.product.variants:`, variant.metafields);
      window.customPricingData.metafields_cache[variantId] = variant.metafields;
      return variant.metafields;
    }
  }
  
  // console.log(`❌ No metafields found in page data for variant ${variantId}`);
  return null;
}

// APC: which custom_prices metafield applies to this visitor, or '' for none.
function getColumnMetafieldKey() {
  const customerTags = window.customerTags || [];

  // Prefer the column the theme itself resolved (apc-price-column.liquid,
  // surfaced as window.apcPriceColumn). Here any logged-in customer gets column
  // 2, tagged or not, so sniffing the tag list would find nothing for them and
  // charge full price while the cart still showed the discount. Tag matching
  // stays below as a fallback in case the global is missing.
  if (window.apcPriceColumn === '4') return 'column_4_price';
  if (window.apcPriceColumn === '2') return 'column_2_price';
  if (customerTags.includes('column2') || customerTags.includes('Column2')) return 'column_2_price';
  if (customerTags.includes('column4') || customerTags.includes('Column4')) return 'column_4_price';
  return '';
}

// Get custom price for cart item using line-item properties (preferred method)
function getCustomPriceForItem(item) {
  const customerTags = window.customerTags || [];
  // Get fallback price per unit (item.price is total price for quantity)
  let fallbackPrice = (item.price / 100) / item.quantity; // Convert to dollars per unit

  // APC — Method 0: the metafield map apc-pricing-globals.liquid renders for
  // the current cart. This is the same value the cart page prints, so checking
  // it first is what guarantees the customer is charged what they were shown.
  // Line-item properties are only a snapshot of the moment the item was added:
  // they are absent for anything added before signing in, and stale if the
  // customer's tags changed since. Properties still cover items added by AJAX
  // after this page rendered, so they remain the fallback below.
  const columnKey = getColumnMetafieldKey();
  if (columnKey) {
    tryGetMetafieldsFromPageData(item.variant_id);
    const mapped = getCustomPriceFromMetafields(item.variant_id, columnKey);
    if (mapped) {
      return mapped;
    }
  }
  
  // console.log(`🔍 Checking custom price for item:`, {
  //   title: item.title || item.product_title,
  //   variant_id: item.variant_id,
  //   quantity: item.quantity,
  //   total_price: item.price / 100,
  //   fallback_price_per_unit: fallbackPrice,
  //   customer_tags: customerTags,
  //   properties: item.properties
  // });
  
  // Method 1: Check line-item properties (most reliable)
  if (item.properties && item.properties._has_custom_pricing === 'true') {
    const propertyCustomPrice = parseFloat(item.properties._custom_price);
    const propertyCustomerTag = item.properties._customer_tag;
    const propertyOriginalPrice = parseFloat(item.properties._original_price);
    
    // console.log(`🏷️ Line-item properties found:`, {
    //   custom_price_per_unit: propertyCustomPrice,
    //   customer_tag: propertyCustomerTag,
    //   original_price_per_unit: propertyOriginalPrice,
    //   has_custom_pricing: item.properties._has_custom_pricing
    // });
    
    if (propertyCustomPrice && propertyCustomPrice > 0) {
      // Properties contain per-unit prices, so they're correct regardless of quantity changes
      const expectedTotal = propertyCustomPrice * item.quantity;
      const actualTotal = item.price / 100;
      
      // console.log(`✅ Using line-item property custom price: $${propertyCustomPrice} per unit (tag: ${propertyCustomerTag})`);
      // console.log(`   Expected total for ${item.quantity} items: $${expectedTotal.toFixed(2)}`);
      // console.log(`   Actual total in cart: $${actualTotal.toFixed(2)}`);
      
      // Verify if the cart total matches expected custom pricing
      if (Math.abs(actualTotal - expectedTotal) > 0.02) { // Allow 2 cent rounding difference
        // console.log(`   ⚠️  Cart total doesn't match expected custom pricing!`);
        // console.log(`   This might indicate quantity was changed in cart after adding item`);
      }
      
      return propertyCustomPrice;
    }
  }
  
  // Method 2: Fallback to metafields approach (for backward compatibility)
  // console.log(`🔄 No line-item properties found, falling back to metafields...`);
  
  // Map customer tags to metafield keys
  const metafieldKey = getColumnMetafieldKey();
  
  // console.log(`🏷️ Customer tags: ${customerTags.join(', ')}`);
  // console.log(`🔑 Mapped metafield key: ${metafieldKey}`);
  
  if (metafieldKey) {
    let metafieldPrice = getCustomPriceFromMetafields(item.variant_id, metafieldKey);
    
    // If no metafield found, try alternative methods
    if (!metafieldPrice) {
      // console.log(`🔄 Trying alternative metafield loading for variant ${item.variant_id}...`);
      tryGetMetafieldsFromPageData(item.variant_id);
      metafieldPrice = getCustomPriceFromMetafields(item.variant_id, metafieldKey);
    }
    
    // console.log(`💰 Metafield price for ${metafieldKey}:`, metafieldPrice);
    
    if (metafieldPrice) {
      // console.log(`✅ Found custom price for variant ${item.variant_id} with key ${metafieldKey}: $${metafieldPrice}`);
      
      // console.log(`📊 Final price calculation for ${item.title}:`);
      // console.log(`   Original total: $${item.price/100} (${item.quantity} × $${(item.price/100)/item.quantity})`);
      // console.log(`   Custom per unit: $${metafieldPrice}`);
      // console.log(`   Custom total: $${(metafieldPrice * item.quantity).toFixed(2)} (${item.quantity} × $${metafieldPrice})`);
      
      return metafieldPrice;
    } else {
      // console.log(`❌ No metafield price found for variant ${item.variant_id} with key ${metafieldKey}`);
    }
  }
  
  // console.log(`📊 No custom pricing found, using fallback price: $${fallbackPrice}`);
  return fallbackPrice;
}

// Prepare draft order data
async function prepareDraftOrderData() {
  try {
    // console.log('🔍 Starting prepareDraftOrderData()...');
    
    // Get cart data
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    window.customPricingData.cart = cart;
    
    // console.log('🔍 Preparing draft order data...');
    // console.log('🛒 Cart items:', cart.items.length);
    // console.log('🛒 Cart data:', cart);
    // console.log('📦 Using line-item properties for custom pricing (no metafields loading needed)');
    
    if (!cart.items || cart.items.length === 0) {
      // console.log('❌ No items in cart');
      return { draftOrder: null, hasCustomPricing: false };
    }
    
    // Prepare line items with applied_discount approach
    const lineItems = [];
    let hasCustomPricing = false;
    
    // console.log('🔍 Processing cart items...');
    cart.items.forEach((item, index) => {
      // console.log(`🔍 Processing item ${index + 1}/${cart.items.length}:`, item);
      // Get original price from line-item properties if available (more accurate)
      let originalPricePerUnit;
      if (item.properties && item.properties._original_price) {
        originalPricePerUnit = parseFloat(item.properties._original_price);
        // console.log(`📋 Using original price from properties: $${originalPricePerUnit}`);
      } else {
        // Fallback to calculated price (may be inaccurate if quantity was changed)
        originalPricePerUnit = (item.price / 100) / item.quantity;
        // console.log(`⚠️ Using calculated original price (may be inaccurate): $${originalPricePerUnit}`);
      }
      
      const customPricePerUnit = getCustomPriceForItem(item); // Already per unit
      const discountPerUnit = originalPricePerUnit - customPricePerUnit;
      
      // console.log(`🔍 Checking pricing for item: ${item.title}`);
      // console.log(`   Quantity: ${item.quantity}`);
      // console.log(`   Original total: $${item.price / 100}`);
      // console.log(`   Original per unit: $${originalPricePerUnit.toFixed(2)} (type: ${typeof originalPricePerUnit})`);
      // console.log(`   Custom per unit: $${customPricePerUnit.toFixed(2)} (type: ${typeof customPricePerUnit})`);
      // console.log(`   Discount per unit: $${discountPerUnit.toFixed(2)}`);
      // console.log(`   Has custom pricing? ${discountPerUnit > 0.01}`); // Allow for small rounding differences
      
      // Create line item with original price
      const lineItem = {
        variant_id: item.variant_id,
        quantity: item.quantity,
        price: originalPricePerUnit.toFixed(2) // Original price per unit in dollars
      };
      
      // Add applied_discount if there's a significant discount
      if (discountPerUnit > 0.01) { // Allow for small rounding differences
        hasCustomPricing = true;
        lineItem.applied_discount = {
          value_type: "fixed_amount",
          value: discountPerUnit.toFixed(2),
          amount: discountPerUnit.toFixed(2)
        };
        // console.log(`✅ Applied discount: $${discountPerUnit.toFixed(2)} per unit`);
        // console.log(`   Final price per unit: $${customPricePerUnit.toFixed(2)}`);
      } else {
        // console.log(`❌ No significant discount for this item`);
      }
      
      lineItems.push(lineItem);
      
      // Store for reference
      window.customPricingData.custom_prices[item.variant_id] = {
        original_price_per_unit: originalPricePerUnit,
        custom_price_per_unit: customPricePerUnit,
        discount_per_unit: discountPerUnit,
        quantity: item.quantity,
        original_total: item.price / 100,
        custom_total: (customPricePerUnit * item.quantity).toFixed(2)
      };
    });
    
    // Get customer ID from Shopify Analytics or other sources
    let customerId = null;
    if (window.ShopifyAnalytics?.meta?.page?.customerId) {
      customerId = parseInt(window.ShopifyAnalytics.meta.page.customerId);
    }
    
    console.log('🔍 Customer data for draft order:');
    console.log('   Customer ID:', customerId);
    console.log('   Customer tags:', window.customerTags);
    console.log('   Tags string:', window.customerTags ? window.customerTags.join(', ') : 'none');
    
    // Prepare draft order object https://shopify.dev/docs/api/admin-rest/2025-01/resources/draftorder#post-draft-orders
    const draftOrder = {
      line_items: lineItems,
      customer: {
        "id": customerId
      }, // Shopify API expects customer_id as Integer, not customer object
      note: cart.note || '',
      tags: window.customerTags ? window.customerTags.join(', ') : '',
      note_attributes: [],
      "use_customer_default_address":true
    };
    
    // Add cart attributes as note attributes (skip old custom pricing data)
    if (cart.attributes) {
      // console.log('📋 Processing cart attributes:');
      Object.keys(cart.attributes).forEach(key => {
        // console.log(`   ${key}: ${cart.attributes[key]}`);
        
        // Skip old custom pricing attributes to avoid conflicts
        if (!key.startsWith('custom_discount_') && !key.startsWith('custom_discount_code_')) {
          draftOrder.note_attributes.push({
            name: key,
            value: cart.attributes[key]
          });
          // console.log(`   ✅ Added: ${key}`);
        } else {
          // console.log(`   🗑️ Skipped old attribute: ${key}`);
        }
      });
    }
    
    // Add reference information to note_attributes (applied_discount is now in line_items)
    cart.items.forEach(item => {
      const customPricePerUnit = getCustomPriceForItem(item);
      
      // Get original price from line-item properties if available (more accurate)
      let originalPricePerUnit;
      if (item.properties && item.properties._original_price) {
        originalPricePerUnit = parseFloat(item.properties._original_price);
      } else {
        // Fallback to calculated price (may be inaccurate if quantity was changed)
        originalPricePerUnit = (item.price / 100) / item.quantity;
      }
      
      const discountPerUnit = originalPricePerUnit - customPricePerUnit;
      
      // Only add note_attributes for items with custom pricing
      if (discountPerUnit > 0.01) {
        const totalDiscountForLine = discountPerUnit * item.quantity;
        
        // console.log(`📋 Adding reference info for variant ${item.variant_id}:`);
        // console.log(`   Original per unit: $${originalPricePerUnit.toFixed(2)}`);
        // console.log(`   Custom per unit: $${customPricePerUnit.toFixed(2)}`);
        // console.log(`   Discount per unit: $${discountPerUnit.toFixed(2)}`);
        // console.log(`   Total line discount: $${totalDiscountForLine.toFixed(2)}`);
        
        // Add reference information (discount is now applied via applied_discount in line_items)
        draftOrder.note_attributes.push({
          name: `original_price_${item.variant_id}`,
          value: originalPricePerUnit.toFixed(2)
        });
        
        draftOrder.note_attributes.push({
          name: `custom_price_${item.variant_id}`,
          value: customPricePerUnit.toFixed(2)
        });
        
        draftOrder.note_attributes.push({
          name: `total_discount_${item.variant_id}`,
          value: totalDiscountForLine.toFixed(2)
        });
      }
    });
    
    // console.log('Draft order prepared:', draftOrder);
    
    return { draftOrder, hasCustomPricing };
    
  } catch (error) {
    console.error('Error preparing draft order:', error);
    return { draftOrder: null, hasCustomPricing: false };
  }
}

// Create draft order via API
async function createDraftOrder(draftOrderData) {
  try {
    // console.log('🚀 Creating draft order with custom pricing...');
    // console.log('Draft order data:', draftOrderData);
    
    // Call proxy server draft-order endpoint
    const response = await fetch('https://collabetl.com/draft-order/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        draft_order: draftOrderData
      })
    });
    
    // console.log('📥 Response status:', response.status);
    
    if (response.ok) {
      const result = await response.json();
      // console.log('✅ Draft order created successfully!', result);
      return result;
    } else {
      const errorText = await response.text();
      console.error('❌ Failed to create draft order:', response.status, errorText);
      return null;
    }
    
  } catch (error) {
    console.error('💥 Error creating draft order:', error);
    return null;
  }
}

// Clear cart after successful draft order creation
async function clearCart() {
  try {
    await fetch('/cart/clear.js', { method: 'POST' });
    // console.log('Cart cleared');
  } catch (error) {
    console.error('Error clearing cart:', error);
  }
}

// APC: busy state for the checkout button. Building the draft order takes a few
// seconds — without this the button looks dead and invites a second click.
// Uses the theme's own convention (.btn--loading swaps the label for the
// .loading__spinner the cart sections render inside the button).
function setCheckoutButtonBusy(button, busy) {
  if (!button) return;
  button.classList.toggle('btn--loading', busy);
  button.setAttribute('aria-busy', busy ? 'true' : 'false');
  button.disabled = busy;
}

// Main checkout function
async function customPricingCheckout(checkoutButton) {
  if (window.customPricingProcessing) {
    // console.log('⚠️ Custom pricing already processing, skipping...');
    return false;
  }

  window.customPricingProcessing = true;
  setCheckoutButtonBusy(checkoutButton, true);
  // console.log('🚀 Starting draft order checkout...');
  // console.log('🏷️ Customer tags:', window.customerTags);
  // console.log('🛒 Current cart data:', window.customPricingData.cart);
  
  try {
    // Initialize data first
    initializeCustomPricingData();
    
    // Prepare draft order data
    // console.log('📋 Calling prepareDraftOrderData()...');
    const result = await prepareDraftOrderData();
    
    // console.log('📋 prepareDraftOrderData() result:', result);
    
    if (!result) {
      // console.log('❌ prepareDraftOrderData() returned null/undefined');
      window.customPricingProcessing = false;
      window.location.href = '/checkout';
      return;
    }
    
    const { draftOrder, hasCustomPricing } = result;

    // console.log(`🚀 Draft order preparation complete:`);
    // console.log(`   hasCustomPricing: ${hasCustomPricing}`);
    // console.log(`   draftOrder exists: ${!!draftOrder}`);
    // console.log(`   draftOrder:`, draftOrder);

    if (hasCustomPricing && draftOrder) {
      // console.log('✅ Creating draft order with custom prices...');
      
      // Create draft order
      const createResult = await createDraftOrder(draftOrder);
      
      if (createResult && createResult.invoice_url) {
        // Clear cart and redirect to invoice with delay to ensure it's ready
        await clearCart();
        
        // APC: the button is already spinning from the click. PROD replaced its
        // innerHTML with "⏳ Preparing invoice..." here, which would have thrown
        // away the spinner markup — and it looked for id="checkout", which no
        // button in this theme has, so it never ran at all.

        // Add delay to ensure invoice is ready
        setTimeout(() => {
          window.location.href = createResult.invoice_url;
        }, 3000);
        
      } else {
        // console.log('Failed to create draft order, proceeding to normal checkout...');
        // console.log('Create result:', createResult);
        window.customPricingProcessing = false;
        window.location.href = '/checkout';
      }
      
    } else {
      // console.log(`❌ Not creating draft order:`);
      // console.log(`   hasCustomPricing: ${hasCustomPricing}`);
      // console.log(`   draftOrder exists: ${!!draftOrder}`);
      // console.log('Proceeding to normal checkout...');
      window.customPricingProcessing = false;
      window.location.href = '/checkout';
    }
    
  } catch (error) {
    console.error('❌ Error in draft order checkout:', error);
    console.error('Error stack:', error.stack);
    window.customPricingProcessing = false;
    window.location.href = '/checkout';
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // console.log('Custom Pricing Draft Orders - Initialized');
  
  initializeCustomPricingData();
  
  // Intercept checkout buttons
  document.addEventListener('click', function(event) {
    const target = event.target;
    
    // APC: closest() instead of matches() — this theme's checkout buttons wrap
    // their label in <span class="btn__text">, so the click target is the span
    // and matches() would miss it, silently sending the customer to the normal
    // checkout at full price.
    const checkoutButton = target.closest(`
      input[name="checkout"],
      button[name="checkout"],
      [href$="checkout"],
      .checkout-button,
      .btn--checkout,
      [data-checkout-button]
    `.replace(/\s+/g, ''));

    if (checkoutButton && !window.customPricingProcessing) {
      if (window.customPricingBypass === true) {
        return;
      }

      // console.log('Checkout button clicked, creating draft order...');
      event.preventDefault();
      event.stopPropagation();

      // Pass the button along so it can show a spinner for the several seconds
      // the draft order takes. The busy state is deliberately never cleared:
      // every path out of here navigates away.
      customPricingCheckout(checkoutButton);
    }
  });
});

// Utility functions
window.bypassCustomPricing = function(bypass = true) {
  window.customPricingBypass = bypass;
  // console.log('Custom pricing bypass:', bypass ? 'enabled' : 'disabled');
};

window.customPricingCheckout = customPricingCheckout;